import React, { useRef, useState } from "react";

import { toursService } from "api/services/tours.service";
import TourPolicyEditor from "components/tour-group/TourPolicyEditor";
import Button from "components/UI/Button";
import { getApiErrorMessage } from "utils/api-error";
import { TOUR_POLICY_FIELD_KEYS } from "utils/constant/tour-policy";
import { extractPolicyFieldError, isRefundPolicyCalculable, isSamePolicyRows, normalizePolicyItems, toPenaltyPolicyPayload, toPolicyFormRows, toRefundPolicyPayload, validatePolicyRows } from "utils/tour-policy";

import type { TourPolicyItem, TourPolicyUpdateRequest, TourRefundPolicyItem } from "api/types/tours.types";
import type { TourPolicyFormRow, TourPolicySaved } from "utils/type/tour-policy";
import "./TourRefundPolicyPanel.scss";

interface TourRefundPolicyPanelProps {
  tourId: number;
  /** 저장된 예약금 환불 규정 원본 — 마운트 시 편집 폼 초기값 (다른 투어는 `key` 로 다시 마운트) */
  refundPolicy: TourRefundPolicyItem[] | null;
  /** 저장된 위약금 규정 원본 — 마운트 시 편집 폼 초기값 */
  penaltyPolicy: TourPolicyItem[] | null;
  /** 저장 성공 후 최신 규정을 넘긴다 */
  onSaved?: (saved: TourPolicySaved) => void;
}

type PolicyErrors = { refund: string | null; penalty: string | null };

const NO_ERRORS: PolicyErrors = { refund: null, penalty: null };

/**
 * 개별 투어 예약금 환불 규정 · 위약금(잔금) 규정 편집 패널 (works-228). 투어 상세 하단에 그대로 노출한다.
 * PATCH /travel-products/{id}/update/ 에 **바뀐 규정만** 싣는다 — 손대지 않은 규정은 생략해
 * 계산값이 없는 과거 예약금 규정 때문에 위약금 저장이 400 으로 막히지 않게 한다.
 */
const TourRefundPolicyPanel: React.FC<TourRefundPolicyPanelProps> = ({ tourId, refundPolicy, penaltyPolicy, onSaved }) => {
  // 첫 렌더부터 저장값을 보여주도록 effect 가 아니라 lazy 초기값으로 채운다.
  const [initialRefundRows, setInitialRefundRows] = useState<TourPolicyFormRow[]>(() => toPolicyFormRows(normalizePolicyItems(refundPolicy)));
  const [initialPenaltyRows, setInitialPenaltyRows] = useState<TourPolicyFormRow[]>(() => toPolicyFormRows(normalizePolicyItems(penaltyPolicy)));
  const [refundRows, setRefundRows] = useState<TourPolicyFormRow[]>(initialRefundRows);
  const [penaltyRows, setPenaltyRows] = useState<TourPolicyFormRow[]>(initialPenaltyRows);
  const [errors, setErrors] = useState<PolicyErrors>(NO_ERRORS);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const refundDirty = !isSamePolicyRows(refundRows, initialRefundRows, "refund");
  const penaltyDirty = !isSamePolicyRows(penaltyRows, initialPenaltyRows, "penalty");
  const storedRefundCalculable = isRefundPolicyCalculable(refundPolicy);

  const handleReset = () => {
    setRefundRows(initialRefundRows);
    setPenaltyRows(initialPenaltyRows);
    setErrors(NO_ERRORS);
    setFormError("");
  };

  const handleSave = async () => {
    if (savingRef.current) return;
    setFormError("");

    if (!refundDirty && !penaltyDirty) {
      setFormError("변경된 내용이 없습니다.");
      return;
    }

    const nextErrors: PolicyErrors = {
      refund: refundDirty ? validatePolicyRows(refundRows, "refund") : null,
      penalty: penaltyDirty ? validatePolicyRows(penaltyRows, "penalty") : null,
    };
    setErrors(nextErrors);
    if (nextErrors.refund || nextErrors.penalty) return;

    const payload: TourPolicyUpdateRequest = {};
    if (refundDirty) payload.refund_policy = toRefundPolicyPayload(refundRows);
    if (penaltyDirty) payload.penalty_policy = toPenaltyPolicyPayload(penaltyRows);

    savingRef.current = true;
    setSaving(true);
    try {
      await toursService.updatePolicies(tourId, payload);
      // 저장된 값(정규화된 payload)을 새 기준값으로 삼아 "변경됨" 표시를 지운다.
      if (payload.refund_policy) {
        const saved = toPolicyFormRows(payload.refund_policy);
        setRefundRows(saved);
        setInitialRefundRows(saved);
      }
      if (payload.penalty_policy) {
        const saved = toPolicyFormRows(payload.penalty_policy);
        setPenaltyRows(saved);
        setInitialPenaltyRows(saved);
      }
      alert("환불 규정이 저장되었습니다.");
      onSaved?.({
        refund_policy: payload.refund_policy ?? refundPolicy,
        penalty_policy: payload.penalty_policy ?? penaltyPolicy,
      });
    } catch (err) {
      const data = (err as { response?: { data?: unknown } })?.response?.data;
      const fieldErrors: PolicyErrors = {
        refund: extractPolicyFieldError(data, TOUR_POLICY_FIELD_KEYS.refund),
        penalty: extractPolicyFieldError(data, TOUR_POLICY_FIELD_KEYS.penalty),
      };
      if (fieldErrors.refund || fieldErrors.penalty) setErrors(fieldErrors);
      else setFormError(getApiErrorMessage(err));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <section className="tour-refund-policy-panel" aria-labelledby="tour-refund-policy-panel-title">
      <div className="tour-refund-policy-panel__header">
        <h3 id="tour-refund-policy-panel-title" className="tour-refund-policy-panel__title">
          환불 규정 편집
        </h3>
        <p className="tour-refund-policy-panel__hint">환불 규정은 이 영역의 저장 버튼으로 따로 저장되며, 위 투어 정보 저장과는 별개입니다.</p>
      </div>

      <div className="tour-refund-policy-panel__body">
        {!storedRefundCalculable && <p className="tour-refund-policy-panel__warning">현재 예약금 환불 규정에 기준일·환불율 값이 없는 항목이 있어 회원 직접 취소가 막혀 있습니다. 모든 항목에 값을 입력해 저장하세요.</p>}
        {formError && (
          <p className="tour-refund-policy-panel__error" role="alert">
            {formError}
          </p>
        )}
        <TourPolicyEditor kind="refund" rows={refundRows} onChange={setRefundRows} error={errors.refund} dirty={refundDirty} />
        <TourPolicyEditor kind="penalty" rows={penaltyRows} onChange={setPenaltyRows} error={errors.penalty} dirty={penaltyDirty} />
      </div>

      <div className="tour-refund-policy-panel__footer">
        <Button variant="outline" onClick={handleReset} disabled={saving || (!refundDirty && !penaltyDirty)}>
          되돌리기
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? "저장 중..." : "환불 규정 저장"}
        </Button>
      </div>
    </section>
  );
};

export default TourRefundPolicyPanel;
